import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { FremdDetail, FremdKarte } from "@kundeninfo/api-client";
import { Arbeitsdialog, schnellknopfDatum, useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import type { Schnellknopf } from "@kundeninfo/ui";
import { Eingabe, Hinweis, Steckbrief } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { formatZeitpunkt } from "../../format.js";
import { berlinerTag, heuteBeschriftung, tagBeschriftung } from "./ticketFormat.js";
import { FREMD_STATUSART, FREMD_SYSTEM, minutenText, standText } from "./fremdSicht.js";
import type { FremdticketsClient } from "../fremd/fremdClient.js";

/**
 * „FÄLLIGKEIT …" EINES FREMDTICKETS (Spec Tickets, Nachtrag 1f, F144): Dialog rechts (`Arbeitsdialog
 * panel="fremdticket"`, 640–960 px, unter 1024 px vollflächig). Steckbrief mit Quelle, Vorgang (Verweis ins
 * Quellsystem), Status, Aufwand, Stand und dem einzigen änderbaren Feld, der Fälligkeit (ohne Uhrzeit). Speichern
 * schickt den gelesenen Stand als `erwartet`; antwortet der Server mit 409, gewinnt die Quelle: Feld und Steckbrief
 * zeigen ihren Stand, der Hinweis nennt ihn, der Dialog bleibt offen. Ungespeicherte Änderung beim Abbrechen →
 * gestapelte Rückfrage. Darunter der Verlauf.
 * Ergänzt nach Spec F144: die Schnellknöpfe der Wiedervorlage (+1 Tag, +1 Woche, +1 Monat, keine; Werktag nach
 * `feiertage`, wie im Ticket), „Stand von 14:02", Zuständig (wenn der Aufrufer die Person kennt), der Befund
 * `FREMDTICKET_UNGUELTIG` am Feld. Setzen ist auch erlaubt, wenn die Quelle keine Fälligkeit hat (`erwartet: null`,
 * Dirk 226). Gespeichert wird ohne eigene Bestätigung (Hausregel: Speichern ist die Bestätigung).
 */
function tag(t: unknown): string {
  return typeof t === "string" ? tagBeschriftung(t) : "ohne";
}

/** Die Kennung kurz („fremd#9002“ statt „gruppe/fremd#9002“): Der volle Pfad zählte sonst im Dreier-Raster des
 *  Steckbriefs dreifach zur Panelbreite (Review 9, M1). Voller Pfad im Sofort-Popup und im zugänglichen Namen. */
export function kurzeKennung(kennung: string): string {
  const i = kennung.lastIndexOf("/");
  return i < 0 ? kennung : kennung.slice(i + 1);
}
function QuellVerweis(p: { k: FremdKarte; system: string }): ReactElement {
  const popup = useSofortPopup<HTMLAnchorElement>(`${p.k.externalKey} in ${p.system} öffnen`);
  return (
    <a ref={popup} className="tfd__quelle" href={p.k.url} target="_blank" rel="noopener noreferrer"
      aria-label={`${p.k.externalKey} in ${p.system} öffnen`}>
      {kurzeKennung(p.k.externalKey)} <span aria-hidden="true">↗</span>
    </a>
  );
}
function verlaufText(e: FremdDetail["ereignisse"][number], system: string): string {
  const d = e.detail;
  if (e.eventType === "DUE_DATE_SET") return `${e.user?.name ?? "Jemand"}: Fälligkeit ${tag(d.von)} → ${tag(d.nach)}`;
  if (e.eventType === "DUE_DATE_CONFLICT") {
    return `In ${system} inzwischen geändert: ${tag(d.quelle)} (gewünscht ${tag(d.gewuenscht)}, ${e.user?.name ?? "jemand"})`;
  }
  return `In ${system} geändert: Fälligkeit ${tag(d.von)} → ${tag(d.nach)}`;
}

export function FremdDialog(p: { client: FremdticketsClient; id: string; onSchliessen: (geaendert: boolean) => void;
                                 /** Berliner Tag (Board: `board.heute`); ohne ihn der heutige. */
                                 heute?: string;
                                 /** Feiertage für die Schnellknöpfe (Board: aus `board.kalender`). */
                                 feiertage?: readonly string[];
                                 /** Name der Person, deren Fremdticket es ist (Steckbrief „Zuständig"). */
                                 zustaendig?: string }):
    ReactElement | null {
  const [d, setD] = useState<FremdDetail | null>(null);
  const [datum, setDatum] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [geaendert, setGeaendert] = useState(false);
  const { bestaetigen, bestaetigung } = useBestaetigung();

  const laden = useCallback(async (): Promise<FremdDetail | null> => {
    try {
      const neu = await p.client.getFremdticket(p.id);
      setD(neu);
      return neu;
    } catch {
      setFehler("Das Fremdticket konnte nicht geladen werden.");
      return null;
    }
  }, [p.client, p.id]);
  useEffect(() => { void laden().then((n) => { if (n !== null) setDatum(n.karte.dueDate ?? ""); }); }, [laden]);

  if (d === null) {
    return fehler === null ? null : (
      <Arbeitsdialog titel="Fremdticket" panel="fremdticket" onAbbrechen={() => p.onSchliessen(geaendert)}>
        <Hinweis art="fehler">{fehler}</Hinweis>
      </Arbeitsdialog>
    );
  }
  const k: FremdKarte = d.karte;
  const system = FREMD_SYSTEM[k.system];
  const unveraendert = datum === (k.dueDate ?? "");
  const heute = p.heute ?? berlinerTag(new Date().toISOString());
  const gesperrt = !k.rueckschreiben || laeuft;
  const setzeDatum = (wert: string) => { setDatum(wert); setFehler(null); setFeldFehler(null); };
  const schnell = (knopf: Schnellknopf) =>
    setzeDatum(schnellknopfDatum(knopf, datum === "" ? null : datum, heute, p.feiertage ?? []) ?? "");

  async function speichern(): Promise<void> {
    setLaeuft(true); setFehler(null); setFeldFehler(null); setHinweis(null);
    try {
      await p.client.setFremdFaelligkeit(k.id, datum === "" ? null : datum, k.dueDate);
      p.onSchliessen(true);
    } catch (f) {
      if (f instanceof ApiError && f.status === 409 && f.conflict !== undefined) {
        const quelle = f.conflict.current as FremdKarte;
        setGeaendert(true);
        setD({ ...d!, karte: quelle });
        setDatum(quelle.dueDate ?? "");
        setHinweis(quelle.dueDate === null
          ? `In ${system} wurde die Fälligkeit inzwischen entfernt. Wer will, speichert erneut.`
          : `In ${system} wurde die Fälligkeit inzwischen auf ${tag(quelle.dueDate)} geändert. Wer will, speichert erneut.`);
        void laden();
      } else if (f instanceof ApiError && f.status === 404) {
        setGeaendert(true);
        setFehler(`Diesen Vorgang gibt es in ${system} nicht mehr.`);
      } else if (f instanceof ApiError && f.fields?.some((x) => x.field === "dueDate") === true) {
        setFeldFehler(f.fields.find((x) => x.field === "dueDate")!.message);
      } else {
        setFehler(f instanceof ApiError ? (f.fields?.[0]?.message ?? f.message) : "Die Fälligkeit ließ sich nicht speichern.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (!unveraendert && !(await bestaetigen({ titel: "Änderung verwerfen?", bestaetigenText: "Verwerfen",
      text: "Die neue Fälligkeit ist noch nicht gespeichert." }))) return;
    p.onSchliessen(geaendert);
  }

  return (
    <Arbeitsdialog titel={`${k.externalKey} ${k.title}`} panel="fremdticket" pfad={<span>Tickets › Fremdticket</span>}
      onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft}
      steckbrief={(
        <Steckbrief art="dialog" className="tfd__steckbrief" titel={`${k.externalKey} ${k.title}`}
          werte={(
            <dl>
              {/* `tfd__frei`: Werte aus Fremdsystem und Konfiguration zählen nicht zur Panelbreite (contain, wie
                  `td__frei` im Ticketpanel); feste Formate (Aufwand, Stand, Fälligkeit) setzen die Spalten. */}
              <dt>Quelle</dt><dd data-frei className="tfd__frei">{system} · {k.instanceLabel}</dd>
              <dt>Vorgang</dt>
              <dd data-frei className="tfd__frei"><QuellVerweis k={k} system={system} /></dd>
              <dt>Status</dt><dd data-frei className="tfd__frei">{FREMD_STATUSART[k.remoteStateKind]}</dd>
              {p.zustaendig !== undefined && <><dt>Zuständig</dt><dd data-frei className="tfd__frei">{p.zustaendig}</dd></>}
              <dt>Aufwand</dt>
              <dd data-frei className="tfd__frei">
                {k.timeSpentMinutes === null || k.timeSpentMinutes === 0 ? "–" : `${minutenText(k.timeSpentMinutes)} erfasst`}
                {k.timeEstimateMinutes !== null && k.timeEstimateMinutes > 0 ? ` · ${minutenText(k.timeEstimateMinutes)} geschätzt` : ""}
              </dd>
              <dt>Abgleich</dt><dd data-frei>{standText(k.fetchedAt)}</dd>
              <dt>Fälligkeit</dt>
              <dd data-frei data-zeile="voll" className="tfd__faelligkeit">
                <Eingabe art="datum" aria-label="Fälligkeit" className="tfd__datum" id="tfd-faelligkeit" name="tfd-faelligkeit"
                  wert={datum} {...(gesperrt ? { gesperrt: true } : {})} {...(feldFehler !== null ? { fehler: feldFehler } : {})}
                  onGeaendert={(e) => setzeDatum(e.wert)} />
                {k.rueckschreiben && (
                  <span className="tfd__schnell">
                    <KiKnopf art="leise" className="tfd__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("TAG")}>+1 Tag</KiKnopf>
                    <KiKnopf art="leise" className="tfd__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("WOCHE")}>+1 Woche</KiKnopf>
                    <KiKnopf art="leise" className="tfd__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("MONAT")}>+1 Monat</KiKnopf>
                    <KiKnopf art="leise" className="tfd__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("KEINE")}>keine</KiKnopf>
                  </span>
                )}
                <span className="tfd__grund">
                  {!k.rueckschreiben ? `Rückschreiben ist für ${k.instanceLabel} nicht eingerichtet.`
                    : datum === "" ? "ohne Fälligkeit: steht oben in „Jetzt · fällig“"
                      : /^\d{4}-\d{2}-\d{2}$/.test(datum) ? heuteBeschriftung(datum) : ""}
                </span>
              </dd>
            </dl>
          )} />
      )}
      meldungen={(
        <>
          {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
          {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
        </>
      )}
      hinweis={<span>Status und Zuweisung ändert man in {system}.</span>}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={gesperrt || unveraendert} onClick={() => { void speichern(); }}>
            Speichern
          </KiKnopf>
          <KiKnopf deaktiviert={laeuft} onClick={() => { void abbrechen(); }}>Abbrechen</KiKnopf>
        </>
      )}>
      <section className="tfd__verlauf" aria-label="Verlauf">
        {d.ereignisse.length === 0 ? <p className="tfd__leer">Noch keine Änderungen.</p> : (
          <ul>{d.ereignisse.map((e) => (
            <li key={e.id}><time dateTime={e.occurredAt}>{formatZeitpunkt(e.occurredAt)}</time> {verlaufText(e, system)}</li>
          ))}</ul>
        )}
      </section>
      {bestaetigung}
    </Arbeitsdialog>
  );
}
